// src/pages/workflow/NodeTypeListPage.tsx
// Browse and manage available node types (integration registry).

import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useWorkflow, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Textarea,
  Badge,
  Card,
  CardContent,
  ErrorAlert,
} from '@react/components';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import {
  nodeTypeCategoryMap,
  credentialTypeMap,
  getEntityId,
} from '@/features/workflow/logic';

const CATEGORY_TABS = [
  { value: 'all', labelKey: 'common.all' },
  { value: 'NODE_TYPE_CATEGORY_TRIGGER', labelKey: 'workflow.nodeCategory.trigger' },
  { value: 'NODE_TYPE_CATEGORY_ACTION', labelKey: 'workflow.nodeCategory.action' },
  { value: 'NODE_TYPE_CATEGORY_CONDITION', labelKey: 'workflow.nodeCategory.condition' },
  { value: 'NODE_TYPE_CATEGORY_TRANSFORMER', labelKey: 'workflow.nodeCategory.transformer' },
  { value: 'NODE_TYPE_CATEGORY_INTEGRATION', labelKey: 'workflow.nodeCategory.integration' },
  { value: 'NODE_TYPE_CATEGORY_UTILITY', labelKey: 'workflow.nodeCategory.utility' },
  { value: 'NODE_TYPE_CATEGORY_AI', labelKey: 'workflow.nodeCategory.ai' },
];

export const NodeTypeListPage = observer(() => {
  const store = useWorkflow();
  const { t } = useI18n();

  const [nodeTypes, setNodeTypes] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selectedType, setSelectedType] = useState<any>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', description: '' });
  const [isLoadingEdit, setIsLoadingEdit] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    store.ListNodeTypes?.({ pagination: { limit: 200, cursor: '', direction: '' } })
      ?.then((res: any) => setNodeTypes(res?.items || store.items || []))
      ?.catch(() => {})
      ?.finally(() => setIsLoading(false));
  }, []);

  const filtered = useMemo(() => {
    let items = nodeTypes;
    if (search) {
      const q = search.toLowerCase();
      items = items.filter(
        (nt: any) =>
          (nt.name || '').toLowerCase().includes(q) ||
          (nt.slug || '').toLowerCase().includes(q) ||
          (nt.description || '').toLowerCase().includes(q),
      );
    }
    if (categoryFilter !== 'all') {
      items = items.filter((nt: any) => nt.category === categoryFilter);
    }
    return items;
  }, [nodeTypes, search, categoryFilter]);

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = { all: nodeTypes.length };
    for (const nt of nodeTypes) {
      const cat = nt.category || 'NODE_TYPE_CATEGORY_UTILITY';
      counts[cat] = (counts[cat] || 0) + 1;
    }
    return counts;
  }, [nodeTypes]);

  // Admin edit/delete — the node-type *registration* write stays a machine
  // call, invoked by product-install / manifest sync per docs/ux/workflow-ia.md
  // "No UI — internal"; only the read/update/delete side of node-type admin
  // is a person-facing surface.
  const openEdit = async () => {
    if (!selectedType) return;
    setEditOpen(true);
    setIsLoadingEdit(true);
    try {
      const full = await store.GetNodeType?.(getEntityId(selectedType));
      setEditForm({ name: full?.name ?? selectedType.name ?? '', description: full?.description ?? selectedType.description ?? '' });
    } catch {
      setEditForm({ name: selectedType.name ?? '', description: selectedType.description ?? '' });
    } finally {
      setIsLoadingEdit(false);
    }
  };

  const saveEdit = async () => {
    if (!selectedType) return;
    setIsSaving(true);
    try {
      const updated = await store.UpdateNodeType?.(getEntityId(selectedType), {
        node_type: { ...selectedType, name: editForm.name.trim(), description: editForm.description },
      } as any);
      setNodeTypes((prev) => prev.map((nt) => (getEntityId(nt) === getEntityId(selectedType) ? { ...nt, ...updated } : nt)));
      setSelectedType((prev: any) => ({ ...prev, ...updated }));
      setEditOpen(false);
    } catch {
      // Error handled by store
    } finally {
      setIsSaving(false);
    }
  };

  const doDelete = async () => {
    if (!selectedType) return;
    setIsDeleting(true);
    try {
      await store.DeleteNodeType?.(getEntityId(selectedType));
      setNodeTypes((prev) => prev.filter((nt) => getEntityId(nt) !== getEntityId(selectedType)));
      setSelectedType(null);
      setConfirmDelete(false);
    } catch {
      // Error handled by store
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <nav className="text-sm mb-2">
          <Link to="/workflows" className="text-muted-foreground hover:text-foreground">{t('workflow.pipelines')}</Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">{t('workflow.nodeTypes')}</span>
        </nav>
        <h1 className="text-2xl font-bold text-foreground">{t('workflow.nodeTypes')}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t('workflow.nodeTypesDesc')}</p>
      </div>

      {/* Category tabs */}
      <div className="flex items-center gap-1 border-b border-border overflow-x-auto">
        {CATEGORY_TABS.map((tab) => (
          <button
            key={tab.value}
            onClick={() => setCategoryFilter(tab.value)}
            className={`
              px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors relative
              ${categoryFilter === tab.value ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}
            `}
          >
            {t(tab.labelKey)}
            {categoryCounts[tab.value] !== undefined && (
              <span className="ml-1 text-xs text-muted-foreground">{categoryCounts[tab.value] || 0}</span>
            )}
            {categoryFilter === tab.value && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-t" />
            )}
          </button>
        ))}
      </div>

      {/* Search */}
      <Input
        placeholder={t('workflow.searchNodeTypes')}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {store.error && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}

      <div className="flex gap-6">
        {/* Node type grid */}
        <div className="flex-1">
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <Card key={i}>
                  <CardContent className="p-4">
                    <div className="animate-pulse space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-muted rounded" />
                        <div className="h-4 bg-muted rounded w-2/3" />
                      </div>
                      <div className="h-3 bg-muted rounded w-full" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-muted-foreground">{t('workflow.noNodeTypesFound')}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {filtered.map((nt: any) => {
                const catInfo = nodeTypeCategoryMap[nt.category] || { color: '#6b7280' };
                const isSelected = selectedType && getEntityId(selectedType) === getEntityId(nt);
                return (
                  <button
                    key={getEntityId(nt) || nt.slug}
                    onClick={() => setSelectedType(nt)}
                    className={`text-left ${isSelected ? '' : ''}`}
                  >
                    <Card className={`h-full transition-all ${isSelected ? 'border-primary ring-2 ring-primary/20' : 'hover:border-primary/30'}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3 mb-2">
                          <div
                            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: (nt.color || catInfo.color) + '20' }}
                          >
                            <span
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: nt.color || catInfo.color }}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium text-sm text-foreground truncate">{nt.name}</h3>
                            <p className="text-xs text-muted-foreground font-mono">{nt.slug}</p>
                          </div>
                        </div>
                        {nt.description && (
                          <p className="text-xs text-muted-foreground line-clamp-2 mb-2">{nt.description}</p>
                        )}
                        <div className="flex items-center gap-2">
                          {nt.is_trigger && <Badge variant="primary" size="sm">{t('workflow.trigger')}</Badge>}
                          {nt.is_builtin && <Badge variant="secondary" size="sm">{t('workflow.builtin')}</Badge>}
                          {nt.credential_type && nt.credential_type !== 'CREDENTIAL_TYPE_UNSPECIFIED' && (
                            <Badge variant="warning" size="sm">{t('workflow.requiresCred')}</Badge>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Detail panel */}
        {selectedType && (
          <div className="w-80 flex-shrink-0">
            <Card className="sticky top-4">
              <CardContent className="p-5 space-y-4">
                <div className="flex items-center gap-3">
                  <div
                    className="w-12 h-12 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: (selectedType.color || '#6b7280') + '20' }}
                  >
                    <span
                      className="w-5 h-5 rounded-full"
                      style={{ backgroundColor: selectedType.color || '#6b7280' }}
                    />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">{selectedType.name}</h3>
                    <p className="text-xs text-muted-foreground font-mono">{selectedType.slug}</p>
                  </div>
                </div>

                {selectedType.description && (
                  <p className="text-sm text-muted-foreground">{selectedType.description}</p>
                )}

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t('workflow.category')}</span>
                    <span className="text-foreground">{(selectedType.category || '').replace('NODE_TYPE_CATEGORY_', '')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t('workflow.isTrigger')}</span>
                    <span className="text-foreground">{selectedType.is_trigger ? t('common.yes') : t('common.no')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t('workflow.isBuiltin')}</span>
                    <span className="text-foreground">{selectedType.is_builtin ? t('common.yes') : t('common.no')}</span>
                  </div>
                  {selectedType.credential_type && selectedType.credential_type !== 'CREDENTIAL_TYPE_UNSPECIFIED' && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t('workflow.credentialType')}</span>
                      <span className="text-foreground">
                        {t(credentialTypeMap[selectedType.credential_type]?.labelKey || selectedType.credential_type)}
                      </span>
                    </div>
                  )}
                </div>

                {/* Ports */}
                {(selectedType.input_ports_definition?.length > 0 || selectedType.output_ports_definition?.length > 0) && (
                  <div className="space-y-2">
                    <h4 className="text-xs font-semibold uppercase text-muted-foreground">{t('workflow.ports')}</h4>
                    {selectedType.input_ports_definition?.map((p: any) => (
                      <div key={p.id} className="flex items-center gap-2 text-xs">
                        <span className="w-2 h-2 rounded-full bg-blue-500" />
                        <span className="text-foreground">{p.name}</span>
                        <span className="text-muted-foreground ml-auto">{t('workflow.input')}</span>
                      </div>
                    ))}
                    {selectedType.output_ports_definition?.map((p: any) => (
                      <div key={p.id} className="flex items-center gap-2 text-xs">
                        <span className="w-2 h-2 rounded-full bg-green-500" />
                        <span className="text-foreground">{p.name}</span>
                        <span className="text-muted-foreground ml-auto">{t('workflow.output')}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Sample data */}
                {selectedType.sample_inputs?.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-1">{t('workflow.sampleInput')}</h4>
                    <pre className="bg-muted rounded-md p-2 text-xs font-mono overflow-x-auto max-h-32">
                      {JSON.stringify(selectedType.sample_inputs[0]?.data || {}, null, 2)}
                    </pre>
                  </div>
                )}

                {/* Admin: edit / delete — not for built-in node types */}
                {!selectedType.is_builtin && (
                  <div className="flex gap-2 pt-3 border-t border-border">
                    <Button size="sm" variant="secondary" onClick={openEdit}>{t('common.edit')}</Button>
                    <Button size="sm" variant="danger" onClick={() => setConfirmDelete(true)}>{t('common.delete')}</Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Edit modal — GetNodeType loads, UpdateNodeType saves */}
      {editOpen && selectedType && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setEditOpen(false)} aria-hidden="true" />
          <div className="relative bg-popover rounded-lg shadow-xl max-w-lg w-full mx-4 p-6 space-y-4">
            <h2 className="text-lg font-semibold text-foreground">{t('workflow.editNodeType')}</h2>
            {isLoadingEdit ? (
              <div className="h-24 animate-pulse rounded-md bg-muted" aria-busy="true" />
            ) : (
              <div className="space-y-4">
                <Input
                  label={t('workflow.field.name')}
                  value={editForm.name}
                  onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))}
                  required
                />
                <Textarea
                  label={t('workflow.field.description')}
                  value={editForm.description}
                  onChange={(e) => setEditForm((p) => ({ ...p, description: e.target.value }))}
                  rows={3}
                />
              </div>
            )}
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="secondary" onClick={() => setEditOpen(false)}>{t('common.cancel')}</Button>
              <Button onClick={saveEdit} disabled={isSaving || isLoadingEdit} isLoading={isSaving}>{t('common.save')}</Button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmDelete}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={doDelete}
        title={t('workflow.deleteNodeTypeConfirmTitle')}
        description={t('workflow.deleteNodeTypeConfirmDesc')}
        confirmLabel={t('common.delete')}
        destructive
        isConfirming={isDeleting}
      />
    </div>
  );
});

export default NodeTypeListPage;
